import moment from 'moment';
import { DASHBOARD_OVERVIEW_QUERY } from '@/constants/urlQuery';
import { mergeSearchParams, parseSearchParams } from '@/utils/urlSearch';

/**
 * Read /dashboard-overview filter fields from a query string.
 * Invalid date ranges are dropped (member/tz are left as-is for validation elsewhere).
 *
 * @param {string} search - `location.search`
 * @returns {{ member: string | null, start: string | null, end: string | null, tz: string | null, team: string | null, status: string | null }}
 */
export function getDashboardOverviewFiltersFromSearch(search) {
  const q = parseSearchParams(search);
  const str = (key) =>
    typeof q[key] === 'string' ? q[key].trim() || null : null;

  let start = str(DASHBOARD_OVERVIEW_QUERY.start);
  let end = str(DASHBOARD_OVERVIEW_QUERY.end);
  const member = str(DASHBOARD_OVERVIEW_QUERY.member);
  const tz = str(DASHBOARD_OVERVIEW_QUERY.tz);
  const team = str(DASHBOARD_OVERVIEW_QUERY.team);
  const status = str(DASHBOARD_OVERVIEW_QUERY.status);

  if (start && !moment(start, 'YYYY-MM-DD', true).isValid()) start = null;
  if (end && !moment(end, 'YYYY-MM-DD', true).isValid()) end = null;
  if (start && end && moment(start).isAfter(end)) {
    start = null;
    end = null;
  }

  return { member, start, end, tz, team, status };
}

/**
 * Payload for mergeSearchParams to set dashboard overview filters.
 * Pass empty string to remove a key.
 */
export function buildDashboardOverviewFilterUpdates({
  memberId,
  teamId,
  start,
  end,
  tz,
  status,
}) {
  return {
    [DASHBOARD_OVERVIEW_QUERY.member]: memberId ?? '',
    [DASHBOARD_OVERVIEW_QUERY.team]: teamId ?? '',
    [DASHBOARD_OVERVIEW_QUERY.start]: start ?? '',
    [DASHBOARD_OVERVIEW_QUERY.end]: end ?? '',
    [DASHBOARD_OVERVIEW_QUERY.tz]: tz ?? '',
    [DASHBOARD_OVERVIEW_QUERY.status]: status ?? '',
  };
}

/**
 * Merges report filter keys into an existing query (keeps cid, flow params, etc.).
 *
 * @param {string} currentSearch - `location.search`
 * @param {{ memberId?: string, teamId?: string, start?: string, end?: string, tz?: string, status?: string }} filters
 * @returns {string} - search string with leading `?` or empty
 */
export function mergeReportFiltersIntoSearch(currentSearch, filters) {
  return mergeSearchParams(
    currentSearch,
    buildDashboardOverviewFilterUpdates({
      memberId: filters.memberId ?? '',
      teamId: filters.teamId ?? '',
      start: filters.start ?? '',
      end: filters.end ?? '',
      tz: filters.tz ?? '',
      status: filters.status ?? '',
    })
  );
}

/**
 * Default date range for report pages: URL start/end if valid, else today.
 *
 * @param {string} [search] - `location.search` or full URL search part
 */
export function getInitialReportDateRangeFromSearch(search = '') {
  const { start, end } = getDashboardOverviewFiltersFromSearch(search);
  const today = moment().format('YYYY-MM-DD');
  return {
    start: start || today,
    end: end || today,
  };
}
